import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import StatusBadge from "@/components/base/StatusBadge";
import PaymentStatusBadge from "@/components/base/PaymentStatusBadge";
import type { Job } from "@/pages/admin/jobs/components/JobModal";
import { getWorkflowStatus, PIPELINE_STEPS, type WorkflowStatus } from "@/lib/workflowStatus";

type Client = {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  address: string;
};

type Contract = {
  id: string;
  contract_number: string;
  title: string;
  total_amount: number;
  deposit_amount: number;
  balance_amount: number;
  status: string;
  payment_status: string;
  event_date: string;
};

type Payment = {
  id: string;
  amount: number;
  status: string;
  payment_method: string;
  created_at: string;
  notes: string | null;
};

type JobStep = {
  id: string;
  title: string;
  status: string;
  sort_order: number;
};

interface Props {
  job: Job;
  tenantId: string;
}



export default function OverviewTab({ job, tenantId }: Props) {
  const navigate = useNavigate();
  const [client, setClient] = useState<Client | null>(null);
  const [contract, setContract] = useState<Contract | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [steps, setSteps] = useState<JobStep[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const [clientRes, stepsRes] = await Promise.all([
        supabase.from("clients").select("id, full_name, email, phone, address").eq("id", job.client_id).eq("tenant_id", tenantId).maybeSingle(),
        supabase.from("job_steps").select("id, title, status, sort_order").eq("job_id", job.id).eq("tenant_id", tenantId).order("sort_order"),
      ]);
      if (!clientRes.error && clientRes.data) setClient(clientRes.data as Client);
      if (!stepsRes.error && stepsRes.data) setSteps(stepsRes.data as JobStep[]);

      if (job.contract_id) {
        const [contractRes, paymentsRes] = await Promise.all([
          supabase
            .from("contracts")
            .select("id, contract_number, title, total_amount, deposit_amount, balance_amount, status, payment_status, event_date")
            .eq("id", job.contract_id)
            .eq("tenant_id", tenantId)
            .maybeSingle(),
          supabase
            .from("payments")
            .select("id, amount, status, payment_method, created_at, notes")
            .eq("contract_id", job.contract_id)
            .eq("tenant_id", tenantId)
            .order("created_at", { ascending: false }),
        ]);
        if (contractRes.data) setContract(contractRes.data as Contract);
        if (paymentsRes.data) setPayments(paymentsRes.data as Payment[]);
      }
      setLoading(false);
    };
    fetchData();
  }, [job.id, job.client_id, job.contract_id, tenantId]);

  const completedSteps = steps.filter((s) => s.status === "completed").length;
  const progressPct = steps.length > 0 ? Math.round((completedSteps / steps.length) * 100) : 0;

  const formatDate = (d: string | null) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  };

  const formatTime = (d: string | null) => {
    if (!d) return "";
    return new Date(d).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  };

  const calendarStatus = job.calendar_sync_status;
  const wfStatus = job.workflow_status || job.status;
  const wfCfg = getWorkflowStatus(wfStatus);
  const currentPipelineIdx = PIPELINE_STEPS.indexOf(wfStatus as WorkflowStatus);

  const timestamps = [
    job.shoot_date ? { label: "Shoot Date", value: job.shoot_date, icon: "ri-camera-3-line", color: "text-sky-500" } : null,
    job.editing_started_at ? { label: "Editing Started", value: job.editing_started_at, icon: "ri-edit-2-line", color: "text-violet-500" } : null,
    job.delivered_at ? { label: "Delivered", value: job.delivered_at, icon: "ri-send-plane-fill", color: "text-emerald-500" } : null,
    job.completed_at ? { label: "Completed", value: job.completed_at, icon: "ri-checkbox-circle-fill", color: "text-emerald-600" } : null,
  ].filter(Boolean) as { label: string; value: string; icon: string; color: string }[];

  // Payment stats
  const paidTotal = payments.filter((p) => p.status === "paid" || p.status === "completed").reduce((s, p) => s + Number(p.amount), 0);
  const pendingTotal = payments.filter((p) => p.status === "pending").reduce((s, p) => s + Number(p.amount), 0);
  const contractTotal = Number(contract?.total_amount ?? 0);
  const contractBalance = Number(contract?.balance_amount ?? 0);
  const paymentPct = contractTotal > 0 ? Math.min(100, Math.round((paidTotal / contractTotal) * 100)) : 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left — 2/3 */}
      <div className="lg:col-span-2 space-y-5">

        {/* Workflow Status Card */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-slate-400 text-xs uppercase tracking-wider mb-4 flex items-center gap-2">
            <i className="ri-git-branch-line text-[#C8A96E] text-xs"></i>
            Workflow Status
          </h3>
          {/* Current status badge */}
          <div className={`flex items-center gap-3 p-3 rounded-xl border mb-4 ${wfCfg.bg} ${wfCfg.border}`}>
            <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${wfCfg.bg} border ${wfCfg.border} flex-shrink-0`}>
              <i className={`${wfCfg.icon} ${wfCfg.color}`}></i>
            </div>
            <div>
              <p className={`text-sm font-semibold ${wfCfg.color}`}>{wfCfg.label}</p>
              <p className="text-xs text-slate-400">{wfCfg.description}</p>
            </div>
          </div>
          {/* Pipeline mini progress */}
          {wfStatus !== "cancelled" && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
                <span>Pipeline</span>
                <span className="font-medium text-[#C8A96E]">
                  {Math.max(0, currentPipelineIdx + 1)}/{PIPELINE_STEPS.length}
                </span>
              </div>
              <div className="flex gap-1">
                {PIPELINE_STEPS.map((step, idx) => {
                  const isDone = idx <= currentPipelineIdx;
                  const isActive = idx === currentPipelineIdx;
                  const sCfg = getWorkflowStatus(step);
                  return (
                    <div
                      key={step}
                      title={sCfg.label}
                      className={`flex-1 h-1.5 rounded-full transition-all ${
                        isDone ? (isActive ? "bg-[#C8A96E]" : "bg-emerald-400") : "bg-slate-200"
                      }`}
                    />
                  );
                })}
              </div>
            </div>
          )}
          {/* Timestamps */}
          {timestamps.length > 0 && (
            <div className="mt-4 space-y-2 pt-4 border-t border-slate-100">
              <p className="text-slate-400 text-xs uppercase tracking-wider mb-2">Timestamps</p>
              {timestamps.map((ts) => (
                <div key={ts.label} className="flex items-center gap-2">
                  <span className={`w-4 h-4 flex items-center justify-center flex-shrink-0 ${ts.color}`}>
                    <i className={`${ts.icon} text-xs`}></i>
                  </span>
                  <span className="text-slate-400 text-xs w-28 shrink-0">{ts.label}</span>
                  <span className="text-slate-600 text-xs">
                    {new Date(ts.value).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    {" "}
                    <span className="text-slate-400">
                      {new Date(ts.value).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Event Info card */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-slate-400 text-xs uppercase tracking-wider mb-4 flex items-center gap-2">
            <i className="ri-camera-3-line text-[#C8A96E] text-xs"></i>
            Event Details
          </h3>
          <div className="grid grid-cols-2 gap-x-8 gap-y-4">
            <div>
              <p className="text-slate-400 text-xs mb-1">Event Type</p>
              <p className="text-[#0f172a] text-sm font-semibold capitalize">{job.job_type}</p>
            </div>
            <div>
              <p className="text-slate-400 text-xs mb-1">Job Status</p>
              <StatusBadge status={job.status} />
            </div>
            <div>
              <p className="text-slate-400 text-xs mb-1">Shoot Date</p>
              <p className="text-[#0f172a] text-sm">{formatDate(job.shoot_date)}</p>
              {job.shoot_date && <p className="text-slate-400 text-xs mt-0.5">{formatTime(job.shoot_date)}</p>}
            </div>
            <div>
              <p className="text-slate-400 text-xs mb-1">Location</p>
              {job.location ? (
                <p className="text-slate-700 text-sm flex items-start gap-1.5">
                  <i className="ri-map-pin-line text-slate-300 text-xs mt-0.5 flex-shrink-0"></i>
                  {job.location}
                </p>
              ) : (
                <p className="text-slate-300 text-sm">—</p>
              )}
            </div>
            <div>
              <p className="text-slate-400 text-xs mb-1">Delivery Status</p>
              <StatusBadge status={job.delivery_status ?? "not_started"} />
              {job.delivered_at && (
                <p className="text-slate-400 text-[10px] mt-1">
                  Delivered {new Date(job.delivered_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </p>
              )}
            </div>
            <div>
              <p className="text-slate-400 text-xs mb-1">Google Calendar</p>
              <span className={`flex items-center gap-1.5 text-xs ${
                calendarStatus === "synced" ? "text-emerald-500" :
                calendarStatus === "sync_error" ? "text-red-500" : "text-slate-300"
              }`}>
                <i className={calendarStatus === "synced" ? "ri-calendar-check-line" : calendarStatus === "sync_error" ? "ri-calendar-close-line" : "ri-calendar-line"}></i>
                {calendarStatus === "synced" ? "Synced" : calendarStatus === "sync_error" ? "Sync Error" : "Not synced"}
              </span>
            </div>
            {job.notes && (
              <div className="col-span-2">
                <p className="text-slate-400 text-xs mb-1">Notes</p>
                <p className="text-slate-600 text-sm leading-relaxed bg-slate-50 rounded-lg p-3 border border-slate-100">{job.notes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Payment Summary */}
        {contract && (
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-slate-400 text-xs uppercase tracking-wider flex items-center gap-2">
                <i className="ri-money-dollar-circle-line text-[#C8A96E] text-xs"></i>
                Payment Summary
              </h3>
              <PaymentStatusBadge status={contract.payment_status} />
            </div>

            {/* Progress bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="text-slate-400">Collected</span>
                <span className="font-semibold text-[#0f172a]">
                  ${paidTotal.toLocaleString()} <span className="text-slate-300">/ ${contractTotal.toLocaleString()}</span>
                </span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${paymentPct}%`,
                    background: paymentPct >= 100 ? "#10b981" : "#C8A96E",
                  }}
                />
              </div>
              <p className="text-slate-300 text-[10px] mt-1 text-right">{paymentPct}% paid</p>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-slate-50 rounded-lg p-3 text-center border border-slate-100">
                <p className="text-[#0f172a] font-bold text-base">${contractTotal.toLocaleString()}</p>
                <p className="text-slate-400 text-[10px] mt-0.5">Total</p>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3 text-center border border-emerald-100">
                <p className="text-emerald-600 font-bold text-base">${paidTotal.toLocaleString()}</p>
                <p className="text-slate-400 text-[10px] mt-0.5">Paid</p>
              </div>
              <div className={`rounded-lg p-3 text-center border ${contractBalance > 0 ? "bg-amber-50 border-amber-100" : "bg-slate-50 border-slate-100"}`}>
                <p className={`font-bold text-base ${contractBalance > 0 ? "text-amber-600" : "text-slate-400"}`}>
                  ${contractBalance.toLocaleString()}
                </p>
                <p className="text-slate-400 text-[10px] mt-0.5">Balance Due</p>
              </div>
            </div>

            {/* Payments list */}
            {payments.length > 0 && (
              <div className="space-y-2 pt-3 border-t border-slate-100">
                <p className="text-slate-400 text-xs uppercase tracking-wider mb-2">Payment History</p>
                {payments.map((p) => (
                  <div key={p.id} className="flex items-center gap-3">
                    <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 text-xs ${
                      p.status === "paid" || p.status === "completed" ? "bg-emerald-100 text-emerald-600" :
                      p.status === "pending" ? "bg-amber-100 text-amber-600" :
                      "bg-slate-100 text-slate-400"
                    }`}>
                      <i className={
                        p.status === "paid" || p.status === "completed" ? "ri-check-line" :
                        p.status === "pending" ? "ri-time-line" : "ri-close-line"
                      }></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-slate-700 text-xs font-medium capitalize">
                        {p.payment_method?.replace(/_/g, " ") || "Payment"}
                        {p.notes && <span className="text-slate-400 font-normal"> — {p.notes}</span>}
                      </p>
                      <p className="text-slate-300 text-[10px]">
                        {new Date(p.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </p>
                    </div>
                    <p className={`text-sm font-semibold flex-shrink-0 ${
                      p.status === "paid" || p.status === "completed" ? "text-emerald-600" :
                      p.status === "pending" ? "text-amber-600" : "text-slate-400"
                    }`}>
                      ${Number(p.amount).toLocaleString()}
                    </p>
                  </div>
                ))}
                {pendingTotal > 0 && (
                  <div className="mt-2 pt-2 border-t border-dashed border-slate-200 flex items-center justify-between">
                    <span className="text-amber-600 text-xs font-medium flex items-center gap-1">
                      <i className="ri-time-line text-xs"></i>
                      Pending
                    </span>
                    <span className="text-amber-600 text-xs font-semibold">${pendingTotal.toLocaleString()}</span>
                  </div>
                )}
              </div>
            )}

            <button
              onClick={() => navigate(`/admin/contracts/${contract.id}`)}
              className="w-full text-xs text-slate-400 hover:text-[#C8A96E] border border-slate-200 hover:border-[#C8A96E]/30 rounded-lg py-2 transition-all cursor-pointer mt-3"
            >
              Manage Payments →
            </button>
          </div>
        )}

        {/* Workflow Steps Progress */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-slate-400 text-xs uppercase tracking-wider flex items-center gap-2">
              <i className="ri-list-check-2 text-[#C8A96E] text-xs"></i>
              Workflow Steps
            </h3>
            <span className="text-[#C8A96E] text-xs font-semibold">{completedSteps}/{steps.length} · {progressPct}%</span>
          </div>
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => <div key={i} className="h-8 bg-slate-100 rounded-lg animate-pulse" />)}
            </div>
          ) : (
            <>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-4">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${progressPct}%`, background: progressPct === 100 ? "#10b981" : "#C8A96E" }}
                />
              </div>
              {steps.length === 0 ? (
                <p className="text-slate-300 text-sm text-center py-4">No steps defined</p>
              ) : (
                <div className="space-y-2">
                  {steps.map((step, idx) => (
                    <div key={step.id} className="flex items-center gap-3">
                      <div className={`w-6 h-6 flex items-center justify-center rounded-full text-xs flex-shrink-0 ${
                        step.status === "completed" ? "bg-emerald-100 text-emerald-600" :
                        step.status === "in_progress" ? "bg-amber-100 text-amber-600" :
                        step.status === "blocked" ? "bg-red-100 text-red-500" :
                        "bg-slate-100 text-slate-400"
                      }`}>
                        {step.status === "completed" ? <i className="ri-check-line text-xs"></i> : <span>{idx + 1}</span>}
                      </div>
                      <p className={`text-sm flex-1 ${step.status === "completed" ? "text-slate-300 line-through" : "text-slate-700"}`}>
                        {step.title}
                      </p>
                      <span className={`text-xs capitalize ${
                        step.status === "completed" ? "text-emerald-500" :
                        step.status === "in_progress" ? "text-amber-500" :
                        step.status === "blocked" ? "text-red-500" :
                        "text-slate-300"
                      }`}>
                        {step.status.replace("_", " ")}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Right — 1/3 */}
      <div className="space-y-5">

        {/* Client */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-slate-400 text-xs uppercase tracking-wider mb-4 flex items-center gap-2">
            <i className="ri-user-line text-[#C8A96E] text-xs"></i>
            Client
          </h3>
          {loading ? (
            <div className="space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-4 bg-slate-100 rounded animate-pulse" />)}</div>
          ) : client ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#C8A96E]/10 flex-shrink-0">
                  <span className="text-[#C8A96E] font-semibold text-sm">{client.full_name.charAt(0).toUpperCase()}</span>
                </div>
                <div>
                  <p className="text-[#0f172a] font-semibold text-sm">{client.full_name}</p>
                  <p className="text-slate-400 text-xs">Client</p>
                </div>
              </div>
              {client.email && (
                <a href={`mailto:${client.email}`} className="flex items-center gap-2 text-slate-500 hover:text-[#0f172a] text-xs transition-colors group">
                  <span className="w-4 h-4 flex items-center justify-center text-slate-300 group-hover:text-[#C8A96E] transition-colors"><i className="ri-mail-line"></i></span>
                  {client.email}
                </a>
              )}
              {client.phone && (
                <a href={`tel:${client.phone}`} className="flex items-center gap-2 text-slate-500 hover:text-[#0f172a] text-xs transition-colors group">
                  <span className="w-4 h-4 flex items-center justify-center text-slate-300 group-hover:text-[#C8A96E] transition-colors"><i className="ri-phone-line"></i></span>
                  {client.phone}
                </a>
              )}
              {client.address && (
                <div className="flex items-start gap-2 text-slate-500 text-xs">
                  <span className="w-4 h-4 flex items-center justify-center text-slate-300 mt-0.5"><i className="ri-map-pin-line"></i></span>
                  {client.address}
                </div>
              )}
              <button
                onClick={() => navigate(`/admin/clients?id=${client.id}`)}
                className="w-full text-xs text-slate-400 hover:text-[#C8A96E] border border-slate-200 hover:border-[#C8A96E]/30 rounded-lg py-2 transition-all cursor-pointer"
              >
                View Client Profile →
              </button>
            </div>
          ) : (
            <p className="text-slate-300 text-sm">No client linked</p>
          )}
        </div>

        {/* Linked Contract */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-slate-400 text-xs uppercase tracking-wider mb-4 flex items-center gap-2">
            <i className="ri-file-text-line text-[#C8A96E] text-xs"></i>
            Contract
          </h3>
          {loading ? (
            <div className="h-4 bg-slate-100 rounded animate-pulse" />
          ) : contract ? (
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C8A96E]/10 flex-shrink-0">
                  <i className="ri-file-text-line text-[#C8A96E] text-sm"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[#0f172a] text-sm font-semibold truncate">{contract.title}</p>
                  <p className="text-slate-400 text-xs font-mono">{contract.contract_number}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <p className="text-slate-400 mb-0.5">Status</p>
                  <StatusBadge status={contract.status} />
                </div>
                <div>
                  <p className="text-slate-400 mb-0.5">Payment</p>
                  <PaymentStatusBadge status={contract.payment_status} />
                </div>
                <div>
                  <p className="text-slate-400 mb-0.5">Total</p>
                  <p className="text-[#0f172a] font-semibold">${Number(contract.total_amount).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-slate-400 mb-0.5">Balance</p>
                  <p className={Number(contract.balance_amount) > 0 ? "text-amber-600 font-semibold" : "text-emerald-600 font-semibold"}>
                    ${Number(contract.balance_amount).toLocaleString()}
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate(`/admin/contracts/${contract.id}`)}
                className="w-full text-xs text-slate-400 hover:text-[#C8A96E] border border-slate-200 hover:border-[#C8A96E]/30 rounded-lg py-2 transition-all cursor-pointer"
              >
                View Contract →
              </button>
            </div>
          ) : (
            <div className="text-center py-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 mx-auto mb-2">
                <i className="ri-file-text-line text-slate-300 text-sm"></i>
              </div>
              <p className="text-slate-400 text-xs">No contract linked</p>
            </div>
          )}
        </div>

        {/* Quick Job Info */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-slate-400 text-xs uppercase tracking-wider mb-4 flex items-center gap-2">
            <i className="ri-information-line text-[#C8A96E] text-xs"></i>
            Job Info
          </h3>
          <div className="space-y-3 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-slate-400">Job ID</span>
              <span className="text-slate-500 font-mono text-[10px] truncate max-w-[120px]">{job.id.slice(0, 8)}…</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-400">Created</span>
              <span className="text-slate-600">
                {new Date(job.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </span>
            </div>
            {job.updated_at && (
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Last Updated</span>
                <span className="text-slate-600">
                  {new Date(job.updated_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-slate-400">Photo Selection</span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border capitalize ${
                job.photo_selection_status === "submitted" ? "bg-amber-50 text-amber-600 border-amber-200" :
                job.photo_selection_status === "reviewed" ? "bg-emerald-50 text-emerald-600 border-emerald-200" :
                job.photo_selection_status === "in_progress" ? "bg-sky-50 text-sky-600 border-sky-200" :
                "bg-slate-100 text-slate-400 border-slate-200"
              }`}>
                {(job.photo_selection_status ?? "not started").replace(/_/g, " ")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}